import Link from "next/link";
import Image from "next/image";
import { FC } from "react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { BsTwitterX } from "react-icons/bs";
import { useCategories } from "@/api/hook/useCategories";

const Footer: FC = () => {
  const { data: categories } = useCategories();
  const aboutLinks = [
    { id: 1, title: "Who We are", link: "" },
    { id: 2, title: "Our Team", link: "" },
    { id: 3, title: "Vacancies", link: "" },
    { id: 4, title: "Become an Affiliate", link: "" },
    { id: 5, title: "News", link: "" },
    { id: 6, title: "Resources", link: "" },
    { id: 7, title: "Contact Us", link: "" },
  ];

  const coursesLinks = (categories ?? [])
    .filter((cat) => cat.categoryName !== "In progress...")
    .map((cat) => ({
      id: cat.id,
      title: cat.categoryName,
      link: `https://student.laplagemetaverse.org/courses/${cat.id}`,
    }));

  return (
    <footer className="bg-[#FFF]">
      <div className="xl:container mx-auto pt-8 sm:pt-12 lg:pt-16 pb-6 sm:pb-8 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo and Social Media Section */}
          <div className="flex flex-col gap-6">
            <Link href="/" className="inline-block">
              <Image
                className="w-40 sm:w-48 lg:w-52"
                alt="logo"
                src="/logo.svg"
                width={500}
                height={500}
                priority
              />
            </Link>
            <div className="flex flex-row gap-4 sm:gap-6">
              {[
                {
                  Icon: FaFacebookF,
                  label: "Facebook",
                  link: "https://www.facebook.com/laplagemetaverse?mibextid=ZbWKwL",
                },
                {
                  Icon: BsTwitterX,
                  label: "Twitter",
                  link: "https://x.com/lpslearning?t=QrF6-25luJPOcksYkQ0CLA&s=09",
                },
                {
                  Icon: FaInstagram,
                  label: "Instagram",
                  link: "https://www.instagram.com/laplageservices?igsh=MXJjMTUzZ3YybHJmaQ==",
                },
              ].map(({ Icon, label, link }) => (
                <Link legacyBehavior key={label} href={link}>
                  <a target="_blank" rel="noopener noreferrer">
                    <button
                      aria-label={label}
                      className="flex justify-center items-center bg-primary hover:bg-primary/90 rounded-full p-2 transition-colors duration-200"
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFF]" />
                    </button>
                  </a>
                </Link>
              ))}
            </div>
          </div>

          {/* About Us Section */}
          <div className="flex flex-col gap-4 sm:gap-5">
            <h3 className="text-primary font-bold text-xl sm:text-2xl">
              About Us
            </h3>
            <nav className="flex flex-col gap-2 sm:gap-3">
              {aboutLinks.map((item) => (
                <Link
                  key={item.id}
                  href={item.link}
                  className="text-base sm:text-lg hover:text-primary transition-colors duration-200"
                >
                  {item.title}
                </Link>
              ))}
            </nav>
          </div>

          {/* Courses Section */}
          <div className="flex flex-col gap-4 sm:gap-5">
            <h3 className="text-primary font-bold text-xl sm:text-2xl">
              Courses
            </h3>
            <nav className="flex flex-col gap-2 sm:gap-3">
              {coursesLinks.map((item) => (
                <Link
                  key={item.id}
                  href={item.link}
                  className="text-base sm:text-lg hover:text-primary transition-colors duration-200"
                >
                  {item.title}
                </Link>
              ))}
            </nav>
          </div>

          {/* Newsletter Section */}
          <div className="flex flex-col gap-4 sm:gap-5">
            <h3 className="text-primary font-bold text-xl sm:text-2xl">
              Subscribe to our newsletter
            </h3>
            <p className="text-base sm:text-lg">
              Get notified about our new premium courses as well as updates to
              news and events.
            </p>
            <form className="flex flex-col gap-3 mt-2">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                className="w-full rounded-md py-2 sm:py-3 px-4 border border-black text-base sm:text-lg 
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                placeholder:text-[#6B7280]"
                aria-label="Email address for newsletter"
              />
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 rounded-md px-5 py-2.5 sm:py-3 
                text-[#FFF] font-medium text-base sm:text-lg transition-colors duration-200"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="mt-10 sm:mt-12 lg:mt-14 pt-6 border-t border-gray-200">
          <p className="text-center text-sm sm:text-base text-[#4B5563]">
            © {new Date().getFullYear()} La Plage Meta Verse. All Rights
            Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
